import { useState } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'src/shared/components/ui/card'
import { Button } from 'src/shared/components/ui/button'
import { Badge } from 'src/shared/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/shared/components/ui/command'
import { Plus, X } from 'lucide-react'
import { useCategoryApi } from 'src/api/db/apiCategory'

type CategoryProductItem = {
  id: number
  category_id: number | null
  product_id: number | null
  category?: { id: number; title: string | null } | null
}

export const Categories = ({
  productId,
  categoryProduct = [],
}: {
  productId: number
  categoryProduct?: CategoryProductItem[]
}) => {
  const { allCategories, createCategoryProduct, deleteCategoryProduct } =
    useCategoryApi({})
  const [addOpen, setAddOpen] = useState(false)

  const assignedCategoryIds = new Set(
    categoryProduct
      .map((cp) => cp.category_id ?? cp.category?.id)
      .filter((id): id is number => id != null)
  )
  const availableToAdd =
    allCategories?.data?.filter((cat) => !assignedCategoryIds.has(cat.id)) ??
    []

  const handleAddCategory = (categoryId: number) => {
    createCategoryProduct.mutate(
      { category_id: categoryId, product_id: productId },
      { onSuccess: () => setAddOpen(false) }
    )
  }

  const handleRemoveCategory = (categoryProductId: number) => {
    deleteCategoryProduct.mutate(categoryProductId)
  }

  const displayTitle = (title: string | null) =>
    title?.split(',')[0]?.trim() ?? ''

  return (
    <Card>
      <CardHeader>
        <CardTitle>Categories</CardTitle>
      </CardHeader>
      <CardContent>
        <Column className="gap-3">
          <Row className="gap-2 flex-wrap items-center">
            {categoryProduct.map((cp) => (
              <Badge
                key={cp.id}
                variant="secondary"
                className="gap-1 pr-1 py-1.5"
              >
                <span>
                  {displayTitle(cp.category?.title ?? null)}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveCategory(cp.id)}
                  disabled={deleteCategoryProduct.isPending}
                  className="rounded-full p-0.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label="Remove category"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <Popover open={addOpen} onOpenChange={setAddOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  disabled={
                    createCategoryProduct.isPending || availableToAdd.length === 0
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add category
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[240px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search categories..." className="h-9" />
                  <CommandList>
                    <CommandEmpty>No category found.</CommandEmpty>
                    <CommandGroup>
                      {availableToAdd.map((cat) => (
                        <CommandItem
                          key={cat.id}
                          value={cat.title ?? ''}
                          onSelect={() => handleAddCategory(cat.id)}
                        >
                          {displayTitle(cat.title)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Row>
          {categoryProduct.length === 0 && availableToAdd.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No categories assigned. Add one above or create categories in
              Categories admin first.
            </p>
          )}
        </Column>
      </CardContent>
    </Card>
  )
}
